/* eslint-disable max-len */
import { StorePlugin, StoreFactory, Vue, Vuex, storeModuleTypes, sessionGetterTypes } from 'vue-infra';
import Config from '@config/config';
import state from './state';
import getters from './getters';
import actions from './actions';
import mutations from './mutations';
import agentList from './modules/agent-list';
import botList from './modules/bot-list';
import conversations from './modules/conversations';
import queueHealth from './modules/queue-health';
import shiftStatus from './modules/shift-status';
import skillList from './modules/skill-list';
import transcript from './modules/transcript';
import alertsHistory from './modules/alerts-history';

Vue.use(Vuex);
Vue.use(StorePlugin, { Vuex });

const storeFactory = new StoreFactory(Config.applicationName, Vue);

const appModule = {
  namespaced: true,
  strict: true,
  state,
  getters,
  actions,
  mutations,
  modules: {
    agentList,
    botList,
    conversations,
    queueHealth,
    shiftStatus,
    transcript,
    skillList,
    alertsHistory,
  },
};

storeFactory.load(appModule);

const rootPath = Config.applicationName;
const agentListModulePath = `${rootPath}/agentList`;
const botListModulePath = `${rootPath}/botList`;
const conversationsModulePath = `${rootPath}/conversations`;
const queueHealthModulePath = `${rootPath}/queueHealth`;
const shiftStatusModulePath = `${rootPath}/shiftStatus`;
const skillListModulePath = `${rootPath}/skillList`;
const transcriptModulePath = `${rootPath}/transcript`;

const store = storeFactory.register();

export default store;

export const START_SUBSCRIPTION_ACTION_NAME = 'startSubscription';
export const STOP_SUBSCRIPTION_ACTION_NAME = 'stopSubscription';
export const UPDATE_SUBSCRIPTION_ACTION_NAME = 'updateSubscription';
// This is used to update Agents filter for agents' list widget
export const CHANGE_AGENTS_FILTER_ACTION_NAME = 'changeAgentsFilter';

export const WIDGET_PATHS = {
  ROOT: rootPath,
  AGENT_LIST: agentListModulePath,
  BOT_LIST: botListModulePath,
  CONVERSATIONS: conversationsModulePath,
  QUEUE_HEALTH: queueHealthModulePath,
  SHIFT_STATUS: shiftStatusModulePath,
  SKILL_LIST: skillListModulePath,
  TRANSCRIPT: transcriptModulePath,
};

export const INFRA_GETTERS = {
  SITE_SETTINGS: `${storeModuleTypes.SESSION}/${sessionGetterTypes.GET_SETTINGS_MAP}`,
  USER_PRIVILEGE: `${storeModuleTypes.SESSION}/${sessionGetterTypes.GET_USER_PRIVILEGE_BY_ID}`,
  FEATURE_STATE: `${storeModuleTypes.SESSION}/${sessionGetterTypes.GET_FEATURE_STATE}`,
  GET_VALUE: `${storeModuleTypes.SESSION}/${sessionGetterTypes.GET_VALUE}`,
  FEATURE: `${storeModuleTypes.SESSION}/${sessionGetterTypes.GET_FEATURE}`,
};

export const INFRA_STORE_VALUES = {
  USER_ID: 'userId',
  ACCOUNT_ID: 'accountId',
  IS_LPA: 'isLPA',
};

// ****** the bellow code in comments does not work well in yarn build mode with IE11
// export const ROOT_GETTER_PATHS = {
//   ROOT_GETTER_ACCOUNT_STATUS_REASONS: `${WIDGET_PATHS.ROOT}/${getters.getStatusReasons.name}`,
//   ROOT_GETTER_ACCOUNT_SKILLS: `${WIDGET_PATHS.ROOT}/${getters.getSkills.name}`,
//   ROOT_GETTER_ACCOUNT_AGENT_GROUPS: `${WIDGET_PATHS.ROOT}/${getters.getAgentGroups.name}`,
//   ROOT_GETTER_USER_MANAGED: `${WIDGET_PATHS.ROOT}/${getters.getUserManaged.name}`,
//   ROOT_GETTER_USER_MANAGED_SKILLS: `${WIDGET_PATHS.ROOT}/${getters.getUserManagedSkills.name}`,
//   ROOT_GETTER_USER_MANAGED_SKILLS_IDS: `${WIDGET_PATHS.ROOT}/${getters.getUserManagedSkillsIds.name}`,
//   ROOT_GETTER_ACCOUNT_USERS: `${WIDGET_PATHS.ROOT}/${getters.getUsers.name}`,
//   ROOT_GETTER_TIMEFRAME_FILTER: `${WIDGET_PATHS.ROOT}/${getters.getTimeFrameFilter.name}`,
//   ROOT_GETTER_FILTERED_USERS: `${WIDGET_PATHS.ROOT}/${getters.getUserIds.name}`,
//   ROOT_GETTER_FILTERED_HUMAN_USERS: `${WIDGET_PATHS.ROOT}/${getHumanUserIds.name}`,
//   ROOT_GETTER_FILTERED_GROUPS_USERS: `${WIDGET_PATHS.ROOT}/${getters.getFilteredGroupsUsersIds.name}`,
//   ROOT_GETTER_BLOCK_CONSUMER_NAME: `${WIDGET_PATHS.ROOT}/${getters.getBlockConsumerName.name}`,
//   ROOT_GETTER_GLOBAL_FILTERS_UPDATING: `${WIDGET_PATHS.ROOT}/${getters.getGlobalFiltersUpdating.name}`,
//   ROOT_GETTER_USE_NEW_MANAGER_API: `${WIDGET_PATHS.ROOT}/${getters.getNewManagerApiState.name}`,
//   ROOT_GETTER_ACCOUNT_DATA_STATUS: `${WIDGET_PATHS.ROOT}/${getters.getAccountDataStatus.name}`,
//   ROOT_GETTER_FETCH_RESPONSE_METRICS: `${WIDGET_PATHS.ROOT}/${getters.getFetchResponseMetricsState.name}`,
//   ROOT_ACTION_GET_NUMERIC_AMW_VERSION: `${WIDGET_PATHS.ROOT}/${getters.getNumericAmwVersion.name}`,
//   ROOT_GETTER_WIDGET_VALID_METRICS: `${WIDGET_PATHS.ROOT}/getWidgetValidMetrics.name`,
//   ROOT_GETTER_WIDGET_DISPLAYED_METRICS: `${WIDGET_PATHS.ROOT}/getWidgetDisplayedMetrics.name`,
//   ROOT_GETTER_WIDGET_METRIC_ATTRIBUTES: `${WIDGET_PATHS.ROOT}/getWidgetMetricAttributes.name`,
//   ROOT_GETTER_GET_WIDGET_PERMISSION: `${WIDGET_PATHS.ROOT}/getWidgetPermission.name`,
//   ROOT_GETTER_HAS_AGENT_PROFILE_ONLY: `${WIDGET_PATHS.ROOT}/hasAgentProfileOnly.name`,
//   ROOT_GETTER_USE_PARTIAL_WIDGETS: `${WIDGET_PATHS.ROOT}/useWorkspacePartialWidgets.name`,
//   ROOT_GETTER_HAS_LIVE_INTENT: `${WIDGET_PATHS.ROOT}/hasLiveIntent.name`,
// };

// export const ROOT_ACTION_PATHS = {
//   ROOT_ACTION_CHANGE_NEW_MANAGER_API_STATE: `${WIDGET_PATHS.ROOT}/${actions.changeNewManagerApiState.name}`,
//   ROOT_ACTION_SET_AMW_VERSION: `${WIDGET_PATHS.ROOT}/${actions.setAmwVersion.name}`,
//   ROOT_ACTION_CHANGE_FETCH_RESPONSE_METRICS_STATE: `${WIDGET_PATHS.ROOT}/${actions.changeFetchResponseMetricsState.name}`,
// };

// ****** this is the code which supports IE11 in yarn build mode
// TODO-CR FIND ANOTHER NAME FOR _ACCOUNT_ (in state too)
export const ROOT_GETTER_PATHS = {
  ROOT_GETTER_ACCOUNT_STATUS_REASONS: `${WIDGET_PATHS.ROOT}/getStatusReasons`,
  ROOT_GETTER_ACCOUNT_SKILLS: `${WIDGET_PATHS.ROOT}/getSkills`,
  ROOT_GETTER_ACCOUNT_AGENT_GROUPS: `${WIDGET_PATHS.ROOT}/getAgentGroups`,
  ROOT_GETTER_USER_MANAGED: `${WIDGET_PATHS.ROOT}/getUserManaged`,
  ROOT_GETTER_USER_MANAGED_SKILLS: `${WIDGET_PATHS.ROOT}/getUserManagedSkills`,
  ROOT_GETTER_USER_MANAGED_SKILLS_IDS: `${WIDGET_PATHS.ROOT}/getUserManagedSkillsIds`,
  ROOT_GETTER_FILTERED_SKILLS_IDS: `${WIDGET_PATHS.ROOT}/getSkillsFilterIds`,
  ROOT_GETTER_ACCOUNT_USERS: `${WIDGET_PATHS.ROOT}/getUsers`,
  ROOT_GETTER_TIMEFRAME_FILTER: `${WIDGET_PATHS.ROOT}/getTimeFrameFilter`,
  ROOT_GETTER_GROUPS_FILTER: `${WIDGET_PATHS.ROOT}/getGroupsFilterIds`,
  ROOT_GETTER_FILTERED_USERS: `${WIDGET_PATHS.ROOT}/getUserIds`,
  ROOT_GETTER_FILTERED_HUMAN_USERS: `${WIDGET_PATHS.ROOT}/getHumanUserIds`,
  ROOT_GETTER_FILTERED_GROUPS_USERS: `${WIDGET_PATHS.ROOT}/getFilteredGroupsUsersIds`,
  ROOT_GETTER_BLOCK_CONSUMER_NAME: `${WIDGET_PATHS.ROOT}/getBlockConsumerName`,
  ROOT_GETTER_GLOBAL_FILTERS_UPDATING: `${WIDGET_PATHS.ROOT}/getGlobalFiltersUpdating`,
  ROOT_GETTER_USE_NEW_MANAGER_API: `${WIDGET_PATHS.ROOT}/getNewManagerApiState`,
  ROOT_GETTER_ACCOUNT_DATA_STATUS: `${WIDGET_PATHS.ROOT}/getAccountDataStatus`,
  ROOT_GETTER_FETCH_RESPONSE_METRICS: `${WIDGET_PATHS.ROOT}/getFetchResponseMetricsState`,
  ROOT_ACTION_GET_NUMERIC_AMW_VERSION: `${WIDGET_PATHS.ROOT}/getNumericAmwVersion`,
  ROOT_GETTER_WIDGET_VALID_METRICS: `${WIDGET_PATHS.ROOT}/getWidgetValidMetrics`,
  ROOT_GETTER_WIDGET_METRIC_ATTRIBUTES: `${WIDGET_PATHS.ROOT}/getWidgetMetricAttributes`,
  ROOT_GETTER_GET_WIDGET_PERMISSION: `${WIDGET_PATHS.ROOT}/getWidgetPermission`,
  ROOT_GETTER_HAS_AGENT_PROFILE_ONLY: `${WIDGET_PATHS.ROOT}/hasAgentProfileOnly`,
  ROOT_GETTER_USE_PARTIAL_WIDGETS: `${WIDGET_PATHS.ROOT}/useWorkspacePartialWidgets`,
  ROOT_GETTER_HAS_LIVE_INTENT: `${WIDGET_PATHS.ROOT}/hasLiveIntent`,
  ROOT_GETTER_HAS_INTENTS_FILTER: `${WIDGET_PATHS.ROOT}/hasLiveIntentsFilter`,
  ROOT_GETTER_USE_BOTS_GRAPH_MOCK: `${WIDGET_PATHS.ROOT}/getUseBotsGraphMock`,
  ROOT_GETTER_BOTS_GRAPH_MOCK: `${WIDGET_PATHS.ROOT}/getBotsGraphMock`,
  ROOT_GETTER_BOTS_GRAPH_USER_SETTINGS: `${WIDGET_PATHS.ROOT}/getBotsGraphUserSettings`,
  ROOT_GETTER_SHOW_BOT_GRAPH: `${WIDGET_PATHS.ROOT}/getShowBotGraph`,
  ROOT_GETTER_ALERT_DATA: `${WIDGET_PATHS.ROOT}/getAlertData`,
  ROOT_GETTER_HAS_FEATURE: `${WIDGET_PATHS.ROOT}/getHasFeature`,
  ROOT_GETTER_HAS_PRIVILEGE: `${WIDGET_PATHS.ROOT}/getHasPrivilege`,
  ROOT_GETTER_GET_VISIBLE_WIDGET: `${WIDGET_PATHS.ROOT}/getVisibleWidgets`,
  ROOT_GETTER_GET_SELECTED_TAB_ID: `${WIDGET_PATHS.ROOT}/getSelectedTabId`,
  ROOT_GETTER_HAS_ALERT_PERMISSIONS: `${WIDGET_PATHS.ROOT}/getAlertPermissions`,
};

export const ROOT_ACTION_PATHS = {
  ROOT_ACTION_CHANGE_NEW_MANAGER_API_STATE: `${WIDGET_PATHS.ROOT}/changeNewManagerApiState`,
  ROOT_ACTION_CHANGE_FETCH_RESPONSE_METRICS_STATE: `${WIDGET_PATHS.ROOT}/changeFetchResponseMetricsState`,
  ROOT_ACTION_SET_AMW_VERSION: `${WIDGET_PATHS.ROOT}/setAmwVersion`,
  ROOT_ACTION_SET_WORKSPACE_PARTIAL_WIDGETS: `${WIDGET_PATHS.ROOT}/setWorkspacePartialWidgets`,
  ROOT_ACTION_CHANGE_ENABLE_INTENTS_STATE: `${WIDGET_PATHS.ROOT}/changeEnableIntentsState`,
  ROOT_ACTION_CHANGE_ENABLE_INTENTS_FILTER_STATE: `${WIDGET_PATHS.ROOT}/changeEnableIntentsFilterState`,
  ROOT_ACTION_SET_USE_BOTS_GRAPH_MOCK: `${WIDGET_PATHS.ROOT}/setUseBotsGraphMock`,
  ROOT_ACTION_SET_BOTS_GRAPH_MOCK: `${WIDGET_PATHS.ROOT}/setBotsGraphMock`,
  ROOT_ACTION_SET_SHOW_BOT_GRAPH: `${WIDGET_PATHS.ROOT}/setShowBotGraph`,
};
